"use client";

import * as React from "react";
import { Loader2, Upload } from "lucide-react";

import { Button } from "@/components/ui/button";

type FileButtonProps = {
  accept?: string;
  onUploadComplete?: (file: File) => void;
  onUpload?: (file: File) => Promise<void>;
  disabled?: boolean;
  isUploading?: boolean;
  setIsUploading: (isUploading: boolean) => void;
  file?: File;
  setFile: (file: File | undefined) => void;
  className?: string;
  label?: string;
};

export function FileButton({
  accept,
  onUploadComplete,
  onUpload,
  disabled = false,
  isUploading,
  setIsUploading,
  file,
  setFile,
  className,
  label = "Upload file",
}: FileButtonProps) {
  const inputRef = React.useRef<HTMLInputElement>(null);

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];

    if (!selectedFile) return;

    setFile(selectedFile);
    setIsUploading(true);

    try {
      if (onUpload) {
        await onUpload(selectedFile);
      }

      onUploadComplete?.(selectedFile);
    } finally {
      setIsUploading(false);
    }

    // Allows selecting the same file again.
    event.target.value = "";
  };

  const handleClick = () => {
    inputRef.current?.click();
  };

  return (
    <div className={className}>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        onChange={handleFileChange}
        disabled={disabled || isUploading}
        className="sr-only"
      />

      <div className="flex items-center gap-3">
        <Button
          type="button"
          className="w-full"
          variant="outline"
          onClick={handleClick}
          disabled={disabled || isUploading}
        >
          {isUploading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Uploading...
            </>
          ) : file ? (
            <span className="truncate">
              <Upload className="mr-2 h-4 w-4" />
              {file.name}
            </span>
          ) : (
            <>
              <Upload className="mr-2 h-4 w-4" />
              {label}
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
